import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import { useNavigate } from "react-router-dom";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text, Icon } from "../elements";
import { formatAmountSuffix } from "../../utils/formatters";

// 1. Static Theme Palettes
const DARK_COLORS = {
  border: "#39496b",
  frame: "#2f3f61",
  title: "#f0f0f0",
  text: "#94a3b8",
  bg: "#1b2b4d",
  cardBg: "#172442",
  innerCardBg: "#1f2e52",
  green: "#10b981",
  red: "#ef4444",
  blue: "#3b82f6",
  amber: "#f59e0b",
  purple: "#8b5cf6",
};

const LIGHT_COLORS = {
  border: "#d1d1d1",
  frame: "#e2e8f0",
  title: "#1e293b",
  text: "#64748b",
  bg: "#ffffff",
  cardBg: "#f8fafc",
  innerCardBg: "#f1f5f9",
  green: "#059669",
  red: "#dc2626",
  blue: "#2563eb",
  amber: "#d97706",
  purple: "#7c3aed",
};

export interface ProjectPnLItem {
  id?: string | number;
  name: string;
  profit: number;
}

export interface PnLDataState {
  totalIncome: number;
  totalExpense: number;
  netProfit: number;
  marginPct: number;
  totalProjectsCount: number;
  profitableCount: number;
  lossCount: number;
  breakEvenCount: number;
  profitableProjects: ProjectPnLItem[];
  lossProjects: ProjectPnLItem[];
}

interface ReduxState {
  user?: {
    user?: {
      company?: {
        id?: string | number;
      };
    };
  };
}

interface PnlProjectRow {
  projectId?: string | number;
  projectName?: string;
  profit?: number | string;
}

export interface PnlSummaryResponse {
  totalIncome?: number | string;
  totalExpense?: number | string;
  netProfit?: number | string;
  marginPct?: number | string;
  totalProjectsCount?: number;
  profitableCount?: number;
  lossMakingCount?: number;
  breakEvenCount?: number;
  topProfitable?: PnlProjectRow[];
  topLossMaking?: PnlProjectRow[];
}

const EMPTY_PNL: PnLDataState = {
  totalIncome: 0,
  totalExpense: 0,
  netProfit: 0,
  marginPct: 0,
  totalProjectsCount: 0,
  profitableCount: 0,
  lossCount: 0,
  breakEvenCount: 0,
  profitableProjects: [],
  lossProjects: [],
};

const toItems = (rows: PnlProjectRow[] = []): ProjectPnLItem[] =>
  rows.map((row, index) => ({
    id: row.projectId || `fallback-${encodeURIComponent(row.projectName || "project")}-${index}`,
    name: row.projectName || "Unnamed Project",
    profit: Number(row.profit ?? 0),
  }));

// Maps the /companies/pnl-summary response onto the card's view state. Totals,
// counts and the (already bounded + ordered) top-N lists come straight from the server.
const mapPnlResponse = (data?: PnlSummaryResponse | null): PnLDataState => {
  if (!data) return EMPTY_PNL;

  return {
    totalIncome: Number(data.totalIncome ?? 0),
    totalExpense: Number(data.totalExpense ?? 0),
    netProfit: Number(data.netProfit ?? 0),
    marginPct: Number(data.marginPct ?? 0),
    totalProjectsCount: Number(data.totalProjectsCount ?? 0),
    profitableCount: Number(data.profitableCount ?? 0),
    lossCount: Number(data.lossMakingCount ?? 0),
    breakEvenCount: Number(data.breakEvenCount ?? 0),
    profitableProjects: toItems(data.topProfitable),
    lossProjects: toItems(data.topLossMaking),
  };
};

interface PnLSummaryChartProps {
  fromDate?: string;
  toDate?: string;
  companyId?: string | number | null;
}

export const PnLSummaryChart: React.FC<PnLSummaryChartProps> = ({ fromDate, toDate, companyId = null }) => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [pnlData, setPnlData] = useState<PnLDataState>(EMPTY_PNL);

  const latestRequestId = useRef<number>(0);
  const userDetails = useSelector((state: ReduxState) => state.user?.user);
  const activeCompanyId = companyId || userDetails?.company?.id;

  const colors = theme !== "light_mode" ? DARK_COLORS : LIGHT_COLORS;

  const fetchPnLSummary = useCallback(async () => {
    const currentRequestId = ++latestRequestId.current;

    try {
      setLoading(true);
      setIsError(false);

      const response = await AnalyticsService.getPnlSummary({
        ...(activeCompanyId ? { companyId: activeCompanyId } : {}),
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
      });

      if (currentRequestId !== latestRequestId.current) return;

      setPnlData(mapPnlResponse(response?.data));
    } catch (err: unknown) {
      if (currentRequestId === latestRequestId.current) {
        console.error("🚀 ~ fetchPnLSummary error:", err);
        setIsError(true);
      }
    } finally {
      if (currentRequestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }, [activeCompanyId, fromDate, toDate]);

  useEffect(() => {
    fetchPnLSummary();
  }, [fetchPnLSummary]);

  const { profitablePct, lossPct, breakEvenPct, pieData } = useMemo(() => {
    const total = pnlData.totalProjectsCount;
    const pPct = total > 0 ? Math.round((pnlData.profitableCount / total) * 100) : 0;
    const lPct = total > 0 ? Math.round((pnlData.lossCount / total) * 100) : 0;
    const bPct = total > 0 ? Math.round((pnlData.breakEvenCount / total) * 100) : 0;

    const chartData = [
      ...(pnlData.profitableCount > 0 ? [{ name: "Profitable", value: pnlData.profitableCount, color: colors.green }] : []),
      ...(pnlData.lossCount > 0 ? [{ name: "Loss-making", value: pnlData.lossCount, color: colors.red }] : []),
      ...(pnlData.breakEvenCount > 0 ? [{ name: "Break-even", value: pnlData.breakEvenCount, color: colors.amber }] : []),
    ];

    return { profitablePct: pPct, lossPct: lPct, breakEvenPct: bPct, pieData: chartData };
  }, [
    pnlData.totalProjectsCount,
    pnlData.profitableCount,
    pnlData.lossCount,
    pnlData.breakEvenCount,
    colors.green,
    colors.red,
    colors.amber,
  ]);

  const hasData = !isError && (pnlData.totalProjectsCount > 0 || pnlData.totalIncome > 0 || pnlData.totalExpense > 0);

  const handleProjectClick = (projectId?: string | number) => {
    if (projectId && !String(projectId).startsWith("fallback-")) {
      navigate(`/projects/${projectId}`);
    }
  };

  return (
    <Box
      className="mc-card"
      sx={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.frame}`,
        borderRadius: "16px",
        p: 2.5,
        mt: 2,
        boxShadow: theme === "light_mode" ? "0px 4px 16px rgba(0, 0, 0, 0.04)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1.5,
          mb: 2,
          borderBottom: `1px solid ${colors.frame}`,
        }}
      >
        <Heading
          as="h2"
          style={{
            fontSize: "18px",
            fontWeight: 600,
            color: colors.title,
          }}
        >
          Profit & Loss Summary
        </Heading>
      </Box>

      {/* Main Content Area */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={36} />
        </Box>
      ) : hasData ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Top 4 Metric Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(4, 1fr)",
              },
              gap: 1.5,
            }}
          >
            {/* Total Income */}
            <Box
              sx={{
                border: `1px solid ${colors.frame}`,
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: colors.cardBg,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "8px",
                  backgroundColor: `${colors.blue}1F`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon className="material-icons" style={{ color: colors.blue, fontSize: "20px" }}>
                  trending_up
                </Icon>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Text
                  style={{
                    fontSize: "11px",
                    color: colors.text,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  Total Income
                </Text>
                <Heading
                  as="h6"
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: colors.title,
                    marginTop: "1px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatAmountSuffix(pnlData.totalIncome, true)}
                </Heading>
              </Box>
            </Box>

            {/* Total Expense */}
            <Box
              sx={{
                border: `1px solid ${colors.frame}`,
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: colors.cardBg,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "8px",
                  backgroundColor: `${colors.amber}1F`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon className="material-icons" style={{ color: colors.amber, fontSize: "20px" }}>
                  account_balance_wallet
                </Icon>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Text
                  style={{
                    fontSize: "11px",
                    color: colors.text,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  Total Expense
                </Text>
                <Heading
                  as="h6"
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: colors.title,
                    marginTop: "1px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatAmountSuffix(pnlData.totalExpense, true)}
                </Heading>
              </Box>
            </Box>

            {/* Net Profit / Loss */}
            <Box
              sx={{
                border: `1px solid ${colors.frame}`,
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: colors.cardBg,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "8px",
                  backgroundColor: pnlData.netProfit >= 0 ? `${colors.green}1F` : `${colors.red}1F`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon
                  className="material-icons"
                  style={{
                    color: pnlData.netProfit >= 0 ? colors.green : colors.red,
                    fontSize: "20px",
                  }}
                >
                  currency_rupee
                </Icon>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Text
                  style={{
                    fontSize: "11px",
                    color: colors.text,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  Net Profit / Loss
                </Text>
                <Heading
                  as="h6"
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: pnlData.netProfit >= 0 ? colors.green : colors.red,
                    marginTop: "1px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatAmountSuffix(pnlData.netProfit, true)}
                </Heading>
              </Box>
            </Box>

            {/* Margin */}
            <Box
              sx={{
                border: `1px solid ${colors.frame}`,
                p: 1.5,
                borderRadius: "10px",
                backgroundColor: colors.cardBg,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "8px",
                  backgroundColor: `${colors.purple}1F`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon className="material-icons" style={{ color: colors.purple, fontSize: "20px" }}>
                  percent
                </Icon>
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Text
                  style={{
                    fontSize: "11px",
                    color: colors.text,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  Profit Margin
                </Text>
                <Heading
                  as="h6"
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: pnlData.marginPct >= 0 ? colors.green : colors.red,
                    marginTop: "1px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {pnlData.marginPct.toFixed(1)}%
                </Heading>
              </Box>
            </Box>
          </Box>

          {/* Performance Overview & Breakdown */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              border: `1px solid ${colors.frame}`,
              borderRadius: "10px",
              backgroundColor: colors.cardBg,
              overflow: "hidden",
            }}
          >
            {/* Donut Chart */}
            <Box sx={{ p: 2, borderRight: { md: `1px solid ${colors.frame}` } }}>
              <Heading
                as="h6"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: colors.text,
                  letterSpacing: "0.5px",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                }}
              >
                Project Performance Overview
              </Heading>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 120,
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {pieData.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={pieData} innerRadius={30} outerRadius={44} paddingAngle={2} dataKey="value">
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box sx={{ position: "absolute", textAlign: "center" }}>
                        <Text
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: colors.title,
                            display: "block",
                            lineHeight: 1,
                          }}
                        >
                          {profitablePct}%
                        </Text>
                        <Text style={{ fontSize: "9px", color: colors.text }}>Profitable</Text>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: "center" }}>
                      <Text style={{ fontSize: "11px", color: colors.text }}>No Projects</Text>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: colors.green,
                      }}
                    />
                    <Box>
                      <Text
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: colors.title,
                        }}
                      >
                        Profitable
                      </Text>
                      <Text style={{ fontSize: "11px", color: colors.green }}>
                        {profitablePct}% ({pnlData.profitableCount} Projects)
                      </Text>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: colors.red,
                      }}
                    />
                    <Box>
                      <Text
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: colors.title,
                        }}
                      >
                        Loss-making
                      </Text>
                      <Text style={{ fontSize: "11px", color: colors.red }}>
                        {lossPct}% ({pnlData.lossCount} Projects)
                      </Text>
                    </Box>
                  </Box>
                  {pnlData.breakEvenCount > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.amber,
                        }}
                      />
                      <Box>
                        <Text
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: colors.title,
                          }}
                        >
                          Break-even
                        </Text>
                        <Text style={{ fontSize: "11px", color: colors.amber }}>
                          {breakEvenPct}% ({pnlData.breakEvenCount} Projects)
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Profitability Counters */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Heading
                as="h6"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: colors.text,
                  letterSpacing: "0.5px",
                  marginBottom: "12px",
                  textTransform: "uppercase",
                }}
              >
                Project Profitability Breakdown
              </Heading>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "8px 12px",
                    borderRadius: "6px",
                    backgroundColor: colors.innerCardBg,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: `${colors.green}1F`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon className="material-icons" style={{ color: colors.green, fontSize: "16px" }}>
                        north_east
                      </Icon>
                    </Box>
                    <Text
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: colors.title,
                      }}
                    >
                      Profitable Projects
                    </Text>
                  </Box>
                  <Text
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: colors.green,
                    }}
                  >
                    {pnlData.profitableCount} Projects
                  </Text>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "8px 12px",
                    borderRadius: "6px",
                    backgroundColor: colors.innerCardBg,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: `${colors.red}1F`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon className="material-icons" style={{ color: colors.red, fontSize: "16px" }}>
                        south_east
                      </Icon>
                    </Box>
                    <Text
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: colors.title,
                      }}
                    >
                      Loss-making Projects
                    </Text>
                  </Box>
                  <Text
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: colors.red,
                    }}
                  >
                    {pnlData.lossCount} Projects
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Profitable vs Loss Project Lists */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              border: `1px solid ${colors.frame}`,
              borderRadius: "10px",
              backgroundColor: colors.cardBg,
              overflow: "hidden",
            }}
          >
            {/* Profitable Projects List */}
            <Box sx={{ p: 2, borderRight: { md: `1px solid ${colors.frame}` } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: colors.green,
                    }}
                  />
                  <Heading
                    as="h6"
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: colors.text,
                    }}
                  >
                    PROFITABLE PROJECTS ({pnlData.profitableCount})
                  </Heading>
                </Box>
                <Text
                  style={{
                    fontSize: "12px",
                    color: colors.green,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                  onClick={() => navigate("/projects?profitability=PROFIT")}
                >
                  View all →
                </Text>
              </Box>

              <Box
                sx={{
                  maxHeight: 180,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: colors.frame,
                    borderRadius: "4px",
                  },
                }}
              >
                {pnlData.profitableProjects.length > 0 ? (
                  pnlData.profitableProjects.map((proj) => {
                    const isClickable = Boolean(proj.id && !String(proj.id).startsWith("fallback-"));

                    return (
                      <Box
                        key={proj.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: "6px 10px",
                          borderRadius: "6px",
                          backgroundColor: colors.bg,
                          cursor: isClickable ? "pointer" : "default",
                          border: `1px solid ${colors.frame}`,
                          gap: 1.5,
                          transition: "all 0.15s ease",
                          "&:hover": isClickable
                            ? {
                                borderColor: colors.green,
                              }
                            : {},
                        }}
                        onClick={() => isClickable && handleProjectClick(proj.id)}
                      >
                        <Text
                          style={{
                            fontSize: "12px",
                            color: colors.title,
                            fontWeight: 500,
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {proj.name}
                        </Text>
                        <Text
                          style={{
                            fontSize: "12px",
                            color: colors.green,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          +{formatAmountSuffix(proj.profit, true)}
                        </Text>
                      </Box>
                    );
                  })
                ) : (
                  <Text style={{ fontSize: "12px", color: colors.text }}>No profitable projects</Text>
                )}
              </Box>
            </Box>

            {/* Loss-making Projects List */}
            <Box sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: colors.red,
                    }}
                  />
                  <Heading
                    as="h6"
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: colors.text,
                    }}
                  >
                    LOSS-MAKING PROJECTS ({pnlData.lossCount})
                  </Heading>
                </Box>
                <Text
                  style={{
                    fontSize: "12px",
                    color: colors.red,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                  onClick={() => navigate("/projects?profitability=LOSS")}
                >
                  View all →
                </Text>
              </Box>

              <Box
                sx={{
                  maxHeight: 180,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: colors.frame,
                    borderRadius: "4px",
                  },
                }}
              >
                {pnlData.lossProjects.length > 0 ? (
                  pnlData.lossProjects.map((proj) => {
                    const isClickable = Boolean(proj.id && !String(proj.id).startsWith("fallback-"));

                    return (
                      <Box
                        key={proj.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: "6px 10px",
                          borderRadius: "6px",
                          backgroundColor: colors.bg,
                          cursor: isClickable ? "pointer" : "default",
                          border: `1px solid ${colors.frame}`,
                          gap: 1.5,
                          transition: "all 0.15s ease",
                          "&:hover": isClickable
                            ? {
                                borderColor: colors.red,
                              }
                            : {},
                        }}
                        onClick={() => isClickable && handleProjectClick(proj.id)}
                      >
                        <Text
                          style={{
                            fontSize: "12px",
                            color: colors.title,
                            fontWeight: 500,
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {proj.name}
                        </Text>
                        <Text
                          style={{
                            fontSize: "12px",
                            color: colors.red,
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {formatAmountSuffix(proj.profit, true)}
                        </Text>
                      </Box>
                    );
                  })
                ) : (
                  <Text style={{ fontSize: "12px", color: colors.text }}>No loss-making projects</Text>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ color: colors.text, fontSize: "15px" }}>No project data available</Text>
        </Box>
      )}
    </Box>
  );
};

export default PnLSummaryChart;
